import { getExtractionVerification } from './assessmentSelectors.js';

// The optional third picture: what the supplied statements actually hold.
//
// Optional is the operative word. Roughly half of assessments arrive with no
// statements at all, and the meeting has to be complete without this. So every
// reader here answers "not available, and here is why" as a first-class result
// rather than as an error, and the view that uses them is skipped rather than
// shown empty.
//
// Two denominators matter more than anything else on this page, and both are
// stated wherever a percentage appears:
//
//   priced value    what the analysis could put a price on, the denominator
//                   for a company's share of the portfolio
//   classified equity   the equity dollars whose style is known, the
//                   denominator for the style grid
//
// Neither is the household's wealth. Statements cover the accounts someone
// happened to send, an unmapped dollar is not an absent exposure, and an
// unclassified fund is not a fund with no style. Saying so is the difference
// between an analysis and a guess with a chart.

// Which buckets are equity. The style grid is an equity instrument: bonds and
// cash are not unclassified equity, they are simply not equity, and putting
// them in the denominator would understate every cell.
const EQUITY_BUCKETS = new Set(['US Equity', 'International Equity']);

export const STYLE_SIZES = ['Large', 'Mid', 'Small'];
export const STYLE_KINDS = ['Value', 'Blend', 'Growth'];
const SIZE_WORDS = { large: 'Large', mid: 'Mid', small: 'Small' };

function num(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function holdingsOf(bundle) {
  return Array.isArray(bundle?.holdings) ? bundle.holdings.filter(h => h && num(h.value) > 0) : [];
}

/**
 * Size and style from the class name, both axes or neither.
 *
 * A holding with a size and no style has no cell. Guessing the missing axis
 * would put real money in a box no classifier ever claimed, which is the single
 * easiest way for this grid to become fiction.
 */
export function parseStyle(assetClass) {
  const words = String(assetClass || '').split(/[^A-Za-z]+/).filter(Boolean).map(w => w.toLowerCase());
  const sizeWord = words.find(w => SIZE_WORDS[w]);
  const size = sizeWord ? SIZE_WORDS[sizeWord] : null;
  const style = STYLE_KINDS.find(k => words.includes(k.toLowerCase())) || null;
  return size && style ? { size, style } : null;
}

/**
 * Whether there is an analysis worth showing, and if not, which kind of nothing.
 *
 * `absent` no statements have been supplied
 * `pending` the read is still running
 * `failed` the read did not complete
 * `unreviewed` results exist but nobody has accepted them
 * `ready` accepted results
 */
export function analysisAvailability({ bundle, status, accepted }) {
  const state = status?.status;
  if (state === 'uploading' || state === 'analyzing') return { state: 'pending', reason: status?.message || '' };
  if (state === 'error') return { state: 'failed', reason: status?.message || 'The analysis did not complete.' };
  if (!bundle || !holdingsOf(bundle).length) return { state: 'absent', reason: '' };

  const verification = getExtractionVerification(bundle);
  if (verification?.needsReview && !accepted) {
    return { state: 'unreviewed', reason: 'The two reads of these statements disagree and need checking.' };
  }
  if (!accepted) return { state: 'unreviewed', reason: 'These results have not been accepted yet.' };
  return { state: 'ready', reason: '' };
}

export function isReady(availability) {
  return availability?.state === 'ready';
}

/**
 * Which batch of statements an acceptance belongs to.
 *
 * Acceptance is recorded against this key rather than as a bare flag, so
 * replacing or clearing the statements invalidates the previous review instead
 * of carrying it silently onto a different set of holdings.
 */
export function batchKey(bundle) {
  const holdings = holdingsOf(bundle);
  if (!holdings.length) return '';
  // The Investment Snapshot built from a batch is kept with it but is not part
  // of what the planner reviewed, so adding it leaves the review standing.
  if (bundle && typeof bundle === 'object' && 'snapshot' in bundle) {
    const { snapshot, ...rest } = bundle; // eslint-disable-line no-unused-vars
    bundle = rest; // eslint-disable-line no-param-reassign
  }
  const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value==='object'
    ? Object.fromEntries(Object.keys(value).sort().filter(k=>k!=='_uid').map(k=>[k,canonical(value[k])])) : value;
  const text=JSON.stringify(canonical(bundle));let h=2166136261;
  for(let i=0;i<text.length;i++)h=Math.imul(h^text.charCodeAt(i),16777619);
  return `batch-v2-${(h>>>0).toString(16)}-${text.length}`;
}

/**
 * Which companies the supplied accounts lead back to, held directly and through
 * funds, without counting a dollar twice.
 *
 * The look-through total already replaces a fund's value with what it holds, so
 * the direct portion is the part of that total the household owns as shares in
 * the company itself. Adding the fund's whole value back on top would double
 * every position it contains, which is the classic way this figure becomes
 * nonsense.
 */
export function getCompanyExposure(bundle, options = {}) {
  const lt = bundle?.lookThrough;
  const companies = Array.isArray(lt?.companies) ? lt.companies : [];
  if (!companies.length) return null;

  const limit = options.limit ?? 10;
  const priced = num(lt.pricedValue);
  const covered = num(lt.coveredValue);

  // A top-level holding of the company itself is the direct part.
  const direct = new Map();
  for (const h of holdingsOf(bundle)) {
    const t = String(h.ticker || '').toUpperCase();
    if (t) direct.set(t, (direct.get(t) || 0) + num(h.value));
  }

  const rows = companies
    .map(c => {
      const tickers = Array.isArray(c.tickers) ? c.tickers.map(t => String(t).toUpperCase()) : [];
      const combined = num(c.value);
      const directValue = Math.min(combined, tickers.reduce((s, t) => s + (direct.get(t) || 0), 0));
      return {
        name: c.name,
        tickers,
        directValue,
        fundValue: Math.max(0, combined - directValue),
        combined,
        pctOfPriced: priced > 0 ? (combined / priced) * 100 : null,
        viaCount: c.viaCount ?? (Array.isArray(c.via) ? c.via.length : 0),
        via: Array.isArray(c.via) ? c.via : [],
      };
    })
    .sort((a, b) => b.combined - a.combined);

  const shown = rows.slice(0, limit);
  const remainingClassified = rows.slice(limit).reduce((s, r) => s + r.combined, 0);

  return {
    rows: shown,
    remainingClassified,
    classifiedValue: covered,
    pricedValue: priced,
    // Not "no exposure": value the analysis could not attribute to a company.
    unmappedValue: Math.max(0, priced - covered),
    coveragePct: num(lt.coveragePct),
    // True when funds could not be looked through, so coverage is a floor and
    // every company figure is "at least this much" rather than "exactly".
    isFloor: Boolean(lt.isFloor),
    fundsAnalyzed: Array.isArray(lt.fundsAnalyzed) ? lt.fundsAnalyzed.length : 0,
    fundsUnavailable: Array.isArray(lt.fundsUnavailable) ? lt.fundsUnavailable : [],
  };
}

/**
 * The nine-cell equity grid, weighted by the equity dollars whose style is known.
 *
 * Three quantities are kept apart and all three are shown: equity with a known
 * style, equity whose style is unknown, and everything that is not equity. Only
 * the first is the denominator.
 */
export function getEquityStyle(bundle) {
  const holdings = holdingsOf(bundle);
  if (!holdings.length) return null;

  let equityValue = 0;
  let classifiedValue = 0;
  let unknownEquityValue = 0;
  const unknown = new Map();
  const cells = new Map();

  for (const h of holdings) {
    const value = num(h.value);
    if (!EQUITY_BUCKETS.has(h.bucket)) continue;
    equityValue += value;
    const style = parseStyle(h.assetClass);
    if (!style) {
      unknownEquityValue += value;
      const key = h.assetClass || 'Unclassified';
      unknown.set(key, (unknown.get(key) || 0) + value);
      continue;
    }
    classifiedValue += value;
    const key = `${style.size}|${style.style}`;
    cells.set(key, (cells.get(key) || 0) + value);
  }

  if (!classifiedValue) {
    return {
      grid: null,
      equityValue,
      classifiedValue: 0,
      unknownEquityValue,
      unknown: [...unknown.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value),
      nonEquityValue: holdings.reduce((s, h) => s + (EQUITY_BUCKETS.has(h.bucket) ? 0 : num(h.value)), 0),
      coveragePct: 0,
    };
  }

  const grid = STYLE_SIZES.map(size =>
    STYLE_KINDS.map(style => {
      const value = cells.get(`${size}|${style}`) || 0;
      return { size, style, value, pct: (value / classifiedValue) * 100 };
    }),
  );

  return {
    grid,
    rowPct: Object.fromEntries(STYLE_SIZES.map((size, i) => [size, grid[i].reduce((s, c) => s + c.pct, 0)])),
    colPct: Object.fromEntries(STYLE_KINDS.map((style, j) => [style, grid.reduce((s, r) => s + r[j].pct, 0)])),
    heaviest: grid.flat().reduce((a, b) => (b.value > a.value ? b : a)),
    equityValue,
    classifiedValue,
    unknownEquityValue,
    unknown: [...unknown.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value),
    nonEquityValue: holdings.reduce((s, h) => s + (EQUITY_BUCKETS.has(h.bucket) ? 0 : num(h.value)), 0),
    coveragePct: equityValue > 0 ? (classifiedValue / equityValue) * 100 : 0,
  };
}

/**
 * A few pairs of facts worth looking at together.
 *
 * Comparisons, never conclusions. Each one states two recorded quantities and
 * the question they raise; none of them says an allocation is wrong, a reserve
 * is short or a retirement is affordable, because none of those follows from
 * two numbers sitting next to each other.
 */
export function getHouseholdContext(facts, bundle, style) {
  const out = [];
  const holdings = holdingsOf(bundle);
  if (!holdings.length) return out;

  const analyzed = holdings.reduce((s, h) => s + num(h.value), 0);
  const known = f => f && f.state === 'known';

  if (known(facts.retirement) && style && style.equityValue > 0) {
    const pct = (style.equityValue / analyzed) * 100;
    out.push({
      key: 'equity-timing',
      left: { label: 'Recorded retirement year', value: String(facts.retirement.value) },
      right: { label: 'Equity share of the analyzed accounts', value: `${pct.toFixed(0)}%` },
      question: 'Which of these accounts would be drawn on first, and when?',
    });
  }

  const cash = holdings
    .filter(h => h.bucket === 'Cash' || /cash|money market/i.test(h.assetClass || ''))
    .reduce((s, h) => s + num(h.value), 0);
  if (known(facts.spending)) {
    out.push({
      key: 'spending-cash',
      left: { label: 'Recorded annual spending', value: facts.spending.value },
      right: { label: 'Cash held in the analyzed accounts', value: cash },
      money: true,
      question: 'Where would the next year of spending come from?',
    });
  }

  if (known(facts.assets)) {
    const recordedInvestments =
      (known(facts.pools?.taxDeferred) ? facts.pools.taxDeferred.value : 0)
      + (known(facts.pools?.roth) ? facts.pools.roth.value : 0)
      + (known(facts.pools?.taxable) ? facts.pools.taxable.value : 0);
    if (recordedInvestments > 0) {
      out.push({
        key: 'coverage',
        left: { label: 'Analyzed from statements', value: analyzed },
        right: { label: 'Recorded investment balances', value: recordedInvestments },
        money: true,
        // Deliberately not called a coverage percentage: the accounts have not
        // been matched to each other, and the two figures can be as of
        // different days.
        question: 'Which recorded accounts do the supplied statements represent?',
      });
    }
  }

  return out;
}

/**
 * When each part of this was true.
 *
 * A statement's valuation date, a fund's disclosure date and the day the data
 * was retrieved are three different things, and an old constituent file is not
 * a current measurement of exposure. Anything absent is named rather than
 * quietly left out.
 */
export function getAnalysisScope(bundle) {
  const accounts = Array.isArray(bundle?.accounts) ? bundle.accounts : [];
  const holdings = holdingsOf(bundle);
  const unsupported = Array.isArray(bundle?.unpricedHoldings) ? bundle.unpricedHoldings : [];
  return {
    asOf: bundle?.asOf || null,
    retrieved: bundle?.retrievedAt || null,
    constituentsAsOf: bundle?.lookThrough?.asOf || null,
    accounts: accounts.map(a => ({
      account: a.account,
      asOf: a.asOf || null,
      value: num(a.value),
      positions: a.positions ?? null,
      weightComplete: a.weightComplete !== false,
    })),
    analyzedValue: holdings.reduce((s, h) => s + num(h.value), 0),
    unsupported: unsupported.map(u => ({ name: u.name, value: num(u.value), account: u.account })),
    unsupportedValue: unsupported.reduce((s, u) => s + num(u.value), 0),
    source: bundle?.source || null,
  };
}

// ---------------------------------------------------------------------------
// Allocation
// ---------------------------------------------------------------------------

// The pie Jake asked for (2026-09-29): US stock, international stock, bonds
// and cash, the way the Morningstar page advisors know draws it, plus the
// stock-to-bond ratio he reads before anything else. Buckets come from the
// classifier; anything it could not place is shown as other, not folded in.
export const ALLOCATION_BUCKETS = [
  { key: 'US Equity', label: 'US stocks', fill: '#015697' },
  { key: 'International Equity', label: 'International stocks', fill: '#3d7fb5' },
  { key: 'Fixed Income', label: 'Bonds', fill: '#c98a1e' },
  { key: 'Cash', label: 'Cash', fill: '#a9c7de' },
  { key: 'Other', label: 'Other', fill: '#8b97a3' },
];

export function getAllocation(bundle) {
  const holdings = (bundle?.holdings || []).filter(h => h && num(h.value) > 0);
  if (!holdings.length) return null;
  const by = {};
  let total = 0;
  for (const h of holdings) {
    const key = ALLOCATION_BUCKETS.some(b => b.key === h.bucket) ? h.bucket : 'Other';
    by[key] = (by[key] || 0) + num(h.value);
    total += num(h.value);
  }
  const parts = ALLOCATION_BUCKETS
    .map(b => ({ ...b, value: by[b.key] || 0, pct: total > 0 ? ((by[b.key] || 0) / total) * 100 : 0 }))
    .filter(p => p.value > 0);
  // Stocks against bonds, with cash and anything unclassified left out of the
  // ratio and shown as their own slices. Cash was once added to bonds, so an
  // all-cash account read as 0 / 100 stocks to bonds beside a donut that was
  // all cash (QA review, 2026-09-30).
  const equity = (by['US Equity'] || 0) + (by['International Equity'] || 0);
  const bonds = by['Fixed Income'] || 0;
  const invested = equity + bonds;
  return {
    parts,
    total,
    equity,
    bonds,
    cash: by.Cash || 0,
    other: by.Other || 0,
    stockPct: invested > 0 ? (equity / invested) * 100 : null,
    bondPct: invested > 0 ? (bonds / invested) * 100 : null,
    cashPct: total > 0 ? ((by.Cash || 0) / total) * 100 : 0,
    usShareOfEquity: equity > 0 ? ((by['US Equity'] || 0) / equity) * 100 : null,
  };
}

/**
 * The rows the look-through table prints: the largest companies, then one row
 * for every other identified company, so nothing identified is dropped.
 */
export function companyRows(exposure, limit = 0) {
  if (!exposure) return { rows: [], rest: 0 };
  const rows = limit > 0 ? exposure.rows.slice(0, limit) : exposure.rows;
  const rest = (exposure.remainingClassified || 0)
    + (limit > 0 ? exposure.rows.slice(limit).reduce((v, r) => v + r.combined, 0) : 0);
  return { rows, rest };
}
